import React, { useCallback, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { createQaAssist } from "../../../actions/qa-assist/createQaAssist";
import Dropzone from "../../../components/forms/GenericDropzone";
import UploadedFilesList from "../../../components/lists/UploadedFilesList";
import PdfPasswordModal from "../../../components/modals/PdfPasswordModal";
import InlineProcessingStatus from "../../../components/processing/InlineProcessingStatus";
import QaResultPanel from "../../../components/qa/QaResultPanel";
import { useRealtimeJob } from "../../../hooks/useRealtimeJob";
import type { JobStatusUpdate } from "../../../hooks/useAppSyncSubscription";
import { useDualUploadForm } from "../../../hooks/useDualUploadForm";
import { getAuthToken } from "../../../utils/getAuthToken";
import QaAssistList from "../../qa-assist/QaAssistList";
import GlassPanel from "../GlassPanel";

interface QaTabProps {
  isApproved: boolean;
  onSendToDocChat?: (combinedText: string) => void;
}

const QaTab: React.FC<QaTabProps> = ({ isApproved, onSendToDocChat }) => {
  const uploadForm = useDualUploadForm({
    primaryMinFiles: 0,
    secondaryMinFiles: 0,
    maxTotalSizeMB: 60,
  });

  const [questionsText, setQuestionsText] = useState("");
  const [answersText, setAnswersText] = useState("");
  const [resultText, setResultText] = useState("");
  const [downloadTarget, setDownloadTarget] = useState("");
  const [isStreamingResponse, setIsStreamingResponse] = useState(false);

  const hasQuestions = uploadForm.primaryFiles.length > 0 || !!questionsText.trim();
  const hasAnswers = uploadForm.secondaryFiles.length > 0 || !!answersText.trim();
  const canRun = hasQuestions && hasAnswers;
  const showResult = !!resultText.trim();

  const uploadHandler = uploadForm.createApiUploadHandler(
    async (primaryKeys, secondaryKeys, filePasswords) =>
      createQaAssist(primaryKeys, secondaryKeys, {
        questionsText,
        answersText,
        additionalContext: "",
        filePasswords,
      }),
    () => {},
  );

  const validateForm = (): boolean => {
    uploadForm.setPrimaryError(hasQuestions ? "" : "Provide questions as text or upload a file.");
    uploadForm.setSecondaryError(hasAnswers ? "" : "Provide reference material as text or upload a file.");
    return hasQuestions && hasAnswers;
  };

  const handleSubmit = (e: React.FormEvent) => uploadForm.handleSubmit(e, validateForm);

  const handleTextExtracted = useCallback((text: string) => {
    setResultText(text);
    setIsStreamingResponse(false);
    uploadForm.setProcessing(false);
    uploadForm.setShowLoader(false);
  }, [uploadForm]);

  const handleDownloadReady = useCallback((target: string) => {
    setDownloadTarget(target);
  }, []);

  const handleClear = useCallback(() => {
    setResultText("");
    setDownloadTarget("");
    setIsStreamingResponse(false);
    setQuestionsText("");
    setAnswersText("");
    uploadForm.clearAllFiles();
    uploadForm.setJobId("");
    uploadForm.setShowLoader(false);
    uploadForm.setProcessing(false);
  }, [uploadForm]);

  const handleLoaderClose = useCallback(() => {
    uploadForm.handleModalClose();
  }, [uploadForm]);

  const handleSendToDocChat = useCallback(() => {
    if (!onSendToDocChat) return;
    const combined = [
      "QUESTIONS:",
      questionsText.trim(),
      "",
      "REFERENCE ANSWERS:",
      answersText.trim(),
      "",
      "AI RESPONSE:",
      resultText.trim(),
    ].join("\n");
    onSendToDocChat(combined);
  }, [onSendToDocChat, questionsText, answersText, resultText]);

  const handleStatusUpdate = useCallback((update: JobStatusUpdate) => {
    if (!uploadForm.jobId || update.jobId !== uploadForm.jobId) {
      return;
    }

    if (update.isStreamingUpdate && update.streamingContent) {
      setIsStreamingResponse(true);
      setResultText((prev) => prev + update.streamingContent!);
      uploadForm.setShowLoader(false);
      uploadForm.setProcessing(false);
      return;
    }

    if (update.status === "COMPLETED" || update.status === "FAILED") {
      setIsStreamingResponse(false);
    }
  }, [uploadForm]);

  useRealtimeJob({
    jobId: uploadForm.jobId || null,
    userId: uploadForm.userId || null,
    getAuthToken,
    enabled: Boolean(uploadForm.jobId && uploadForm.userId && (uploadForm.processing || uploadForm.showLoader || isStreamingResponse)),
    onUpdate: handleStatusUpdate,
    onCompleted: async () => {
      setIsStreamingResponse(false);
    },
    onFailed: async () => {
      setIsStreamingResponse(false);
    },
  });

  return (
    <>
      <Typography
        sx={{
          fontFamily: "'Syne', sans-serif",
          fontSize: "0.8rem",
          fontWeight: 700,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "var(--teal)",
          mb: 0.5,
        }}
      >
        Q&amp;A Assist
      </Typography>
      <Typography sx={{ fontSize: "0.8rem", color: "var(--t2)", mb: 2, lineHeight: 1.55 }}>
        Paste <strong style={{ color: "var(--t1)" }}>questions</strong> in the first box and{" "}
        <strong style={{ color: "var(--t1)" }}>reference answers or context</strong> in the second — or upload a
        document for either.
      </Typography>

      {!showResult && (
        <Box sx={{ opacity: uploadForm.processing ? 0.65 : 1, pointerEvents: uploadForm.processing ? "none" : "auto" }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <GlassPanel>
                  <Typography
                    sx={{
                      fontSize: "0.76rem",
                      fontWeight: 600,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "var(--teal)",
                      mb: 1,
                    }}
                  >
                    Questions
                  </Typography>
                  <Dropzone
                    onDrop={uploadForm.createPrimaryDropHandler(isApproved)}
                    multiple
                    mainText="Upload Questions (Optional)"
                    subText="PDF or DOCX"
                  />
                  <UploadedFilesList
                    title="Question Files"
                    files={uploadForm.primaryFiles}
                    onRemoveFile={uploadForm.removePrimaryFile}
                    filePasswords={uploadForm.filePasswords}
                  />
                  <TextField
                    fullWidth
                    multiline
                    minRows={8}
                    placeholder={"Paste your questions here…\n\ne.g.\n1. What was the date of the incident?\n2. Who was present?"}
                    value={questionsText}
                    onChange={(e) => setQuestionsText(e.target.value)}
                    sx={{ mt: 1 }}
                  />
                  {uploadForm.primaryError && (
                    <Typography color="error" sx={{ mt: 1, fontSize: "0.78rem" }}>
                      {uploadForm.primaryError}
                    </Typography>
                  )}
                </GlassPanel>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <GlassPanel>
                  <Typography
                    sx={{
                      fontSize: "0.76rem",
                      fontWeight: 600,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "var(--teal)",
                      mb: 1,
                    }}
                  >
                    Reference Answers / Context
                  </Typography>
                  <Dropzone
                    onDrop={uploadForm.createSecondaryDropHandler(isApproved)}
                    multiple
                    mainText="Upload Reference Files (Optional)"
                    subText="PDF or DOCX"
                  />
                  <UploadedFilesList
                    title="Reference Files"
                    files={uploadForm.secondaryFiles}
                    onRemoveFile={uploadForm.removeSecondaryFile}
                    filePasswords={uploadForm.filePasswords}
                  />
                  <TextField
                    fullWidth
                    multiline
                    minRows={8}
                    placeholder="Paste answers, notes, or context material here…"
                    value={answersText}
                    onChange={(e) => setAnswersText(e.target.value)}
                    sx={{ mt: 1 }}
                  />
                  {uploadForm.secondaryError && (
                    <Typography color="error" sx={{ mt: 1, fontSize: "0.78rem" }}>
                      {uploadForm.secondaryError}
                    </Typography>
                  )}
                </GlassPanel>
              </Grid>
            </Grid>

            {!canRun && (hasQuestions || hasAnswers) && (
              <Typography sx={{ fontSize: "0.74rem", color: "var(--warn)", mt: 1 }}>
                Please also add {hasQuestions ? "reference answers / context" : "questions"} to generate a response.
              </Typography>
            )}

            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={!isApproved || !canRun || uploadForm.processing}
                sx={{ flex: 3 }}
              >
                Generate Response
              </Button>
              <Button variant="outlined" size="large" onClick={handleClear} sx={{ flex: 1 }}>
                Clear
              </Button>
            </Box>
          </form>
        </Box>
      )}

      <InlineProcessingStatus
        open={uploadForm.showLoader}
        files={[...uploadForm.primaryFiles, ...uploadForm.secondaryFiles]}
        jobId={uploadForm.jobId}
        userId={uploadForm.userId}
        onUpload={uploadHandler}
        // #611 — clearing `processing` only in handleTextExtracted is not
        // enough: that fires from the loader's preview effect, which is skipped
        // when the finished job has no output text (empty result, or output
        // metadata without a contentTextKey). In that state the tab stayed
        // frozen — the submit button and the whole form are gated on
        // `processing`. Completion is the signal that must always clear it.
        onComplete={async () => uploadForm.setProcessing(false)}
        onTextExtracted={handleTextExtracted}
        onDownloadReady={handleDownloadReady}
        onClose={handleLoaderClose}
      />

      {showResult && (
        <QaResultPanel
          responseText={resultText}
          isStreamingResponse={isStreamingResponse}
          downloadTarget={downloadTarget}
          onSendToDocChat={onSendToDocChat ? handleSendToDocChat : undefined}
          onClear={handleClear}
        />
      )}

      <PdfPasswordModal
        open={uploadForm.showPasswordModal}
        file={uploadForm.currentFileNeedingPassword}
        onSubmit={uploadForm.handlePasswordSubmit}
        onSkip={uploadForm.handlePasswordSkip}
        onCancel={uploadForm.handlePasswordCancel}
      />

      <Box sx={{ mt: 3 }}>
        <Typography
          sx={{
            fontFamily: "'Syne', sans-serif",
            fontSize: "0.8rem",
            fontWeight: 700,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "var(--teal)",
            mb: 1,
          }}
        >
          Recent Q&amp;A Results
        </Typography>
        <GlassPanel>
          <QaAssistList
            refresh={uploadForm.refresh}
            onRefreshComplete={uploadForm.handleRefreshComplete}
          />
        </GlassPanel>
      </Box>
    </>
  );
};

export default QaTab;
